import React from 'react';
import { Card, CardBody, CardTitle, CardText, CardSubtitle } from 'reactstrap';
import { Notification, NotificationGroup } from "@progress/kendo-react-notification";
import { Fade } from "@progress/kendo-react-animation";

const FileNoteAlertContext = React.createContext(null);

export const useFileNoteAlerts = () => {
    const context = React.useContext(FileNoteAlertContext);
    if (!context) {
        throw new Error('useFileNoteAlerts must be used within a FileNoteAlertProvider');
    }
    return context;
};

export const FileNoteAlertProvider = ({ children }) => {
    const [fileNoteAlerts, setFileNoteAlerts] = React.useState([]);
    const [viewedContacts, setViewedContacts] = React.useState(new Set());
    const [viewedCompanies, setViewedCompanies] = React.useState(new Set());
    const [activeNotifications, setActiveNotifications] = React.useState(new Map());
    const [pendingContactIDs, setPendingContactIDs] = React.useState(new Set());
    const [pendingCompanyIDs, setPendingCompanyIDs] = React.useState(new Set());

    const registerFileNoteAlerts = React.useCallback((newAlerts) => {
        setFileNoteAlerts(prevAlerts => {
            const existingAlertsMap = new Map(prevAlerts.map(alert => [alert.ID, alert]));
            newAlerts.forEach(newAlert => {
                existingAlertsMap.set(newAlert.ID, newAlert);
            });
            return Array.from(existingAlertsMap.values());
        });
    }, []);

    const hasAlertsForContact = React.useCallback((contactID) => {
        return fileNoteAlerts.some(alert => alert.ContactID === contactID);
    }, [fileNoteAlerts]);

    const hasAlertsForCompany = React.useCallback((companyID) => {
        return fileNoteAlerts.some(alert => alert.CompanyID === companyID);
    }, [fileNoteAlerts]);

    const getAlertsForContact = React.useCallback((contactID) => {
        return fileNoteAlerts.filter(alert => alert.ContactID === contactID);
    }, [fileNoteAlerts]);

    const getAlertsForCompany = React.useCallback((companyID) => {
        return fileNoteAlerts.filter(alert => alert.CompanyID === companyID);
    }, [fileNoteAlerts]);

    const handleNotificationClose = React.useCallback((key) => {
        if (key.startsWith("contact-")) {
            const contactID = key.split("-")[1];
            setViewedContacts(prev => new Set([...prev, contactID]));
        } else if (key.startsWith("company-")) {
            const companyID = key.split("-")[1];
            setViewedCompanies(prev => new Set([...prev, companyID]));
        }
        setActiveNotifications(prev => {
            const newMap = new Map(prev);
            newMap.delete(key);
            return newMap;
        });
    }, []);

    const contactViewed = React.useCallback((contactID) => {
        if (viewedContacts.has(contactID)) return;
        if (!activeNotifications.has(`contact-${contactID}`)) {
            setPendingContactIDs(prev => new Set([...prev, contactID]));
        }
    }, [viewedContacts, activeNotifications]);

    const companyViewed = React.useCallback((companyID) => {
        if (viewedCompanies.has(companyID)) return;
        if (!activeNotifications.has(`company-${companyID}`)) {
            setPendingCompanyIDs(prev => new Set([...prev, companyID]));
        }
    }, [viewedCompanies, activeNotifications]);

    React.useEffect(() => {
        if (pendingContactIDs.size === 0) return;
        pendingContactIDs.forEach(contactID => {
            const contactAlerts = fileNoteAlerts.filter(alert =>
                alert.ContactID === contactID
            );
            if (contactAlerts.length > 0 && !activeNotifications.has(`contact-${contactID}`)) {
                setActiveNotifications(prev => new Map([
                    ...prev,
                    [`contact-${contactID}`, contactAlerts]
                ]));
            }
        });
        setPendingContactIDs(new Set());
    }, [fileNoteAlerts, pendingContactIDs, activeNotifications]);

    React.useEffect(() => {
        if (pendingCompanyIDs.size === 0) return;
        pendingCompanyIDs.forEach(companyID => {
            const companyAlerts = fileNoteAlerts.filter(alert =>
                alert.CompanyID === companyID
            );
            if (companyAlerts.length > 0 && !activeNotifications.has(`company-${companyID}`)) {
                setActiveNotifications(prev => new Map([
                    ...prev,
                    [`company-${companyID}`, companyAlerts]
                ]));
            }
        });
        setPendingCompanyIDs(new Set());
    }, [fileNoteAlerts, pendingCompanyIDs, activeNotifications]);

    const value = {
        fileNoteAlerts,
        registerFileNoteAlerts,
        contactViewed,
        companyViewed,
        hasAlertsForContact,
        hasAlertsForCompany,
        getAlertsForContact,
        getAlertsForCompany,
    };

    return (
        <FileNoteAlertContext.Provider value={value}>
            {children}
            <NotificationGroup
                style={{
                    right: 0,
                    bottom: 0,
                    alignItems: "flex-start",
                    flexWrap: "wrap-reverse",
                }}
            >
                {Array.from(activeNotifications.entries()).map(([key, alerts]) => (
                    <Fade
                        key={key}
                        transitionEnterDuration={800}
                        transitionExitDuration={1000}
                    >
                        <Notification
                            key={key}
                            type={{ style: 'info', icon: false }}
                            closable={true}
                            onClose={() => handleNotificationClose(key)}
                        >
                            <AlertsDisplay alerts={alerts} />
                        </Notification>
                    </Fade>
                ))}
            </NotificationGroup>
        </FileNoteAlertContext.Provider>
    );
};


export const AlertsDisplay = ({ alerts }) => {
    const style = { color: "#000000c9" };

    if (!alerts || alerts.length === 0) {
        return (
            <Card style={style} className="text-muted p-3">
                <CardText>No active alerts</CardText>
            </Card>
        );
    }

    return (
        <div
            className="d-flex flex-column gap-3"
            style={{ maxHeight: '400px', overflowY: 'auto', ...style }}
        >
            {alerts.map(alert => {
                const info = [];
                if (alert.Company) info.push(`Company: ${alert.Company}`);
                if (alert.Contact) info.push(`Contact: ${alert.Contact}`);

                return (
                    <Card key={alert.ID}>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                                <CardTitle tag="h5">
                                    {alert.Description || alert.Reference || 'Alert'}
                                </CardTitle>
                                <small className="text-muted">
                                    {new Date(alert.Date).toLocaleDateString()}
                                </small>
                            </div>
                            {info.length > 0 && (
                                <CardSubtitle className="mb-2 text-muted">
                                    {info.join(" | ")}
                                </CardSubtitle>
                            )}
                            <div
                                dangerouslySetInnerHTML={{ __html: alert.Notes }}
                                style={{
                                    maxHeight: '150px',
                                    overflowY: 'auto',
                                    padding: '8px',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '4px'
                                }}
                            />
                        </CardBody>
                    </Card>
                );
            })}
        </div>
    );
};